import type { SavedMatch } from '@texas42/app-model';
import {
  createViewProjection,
  GAME_PHASES,
  type GameView,
} from '@texas42/game-runtime';
import type { SessionMode } from '../app/types.js';
import { evaluateSharePolicy } from './share-policy.js';
import type { GameSessionView, OneHandSessionState } from './types.js';

export interface BuildGameSessionViewInput {
  view: GameView;
  sessionId: string;
  mode: SessionMode;
  matchId: string | null;
  stateRevision: number;
  paused: boolean;
  bidRevealActive: boolean;
  aiEnabled: boolean;
  savedMatch: SavedMatch | null;
  findingSeed: boolean;
  testMode?: boolean;
}

function buildOneHandState(view: GameView): OneHandSessionState {
  const isComplete = view.state.phase === GAME_PHASES.ONE_HAND_COMPLETE;
  return {
    complete: isComplete,
    seed: view.state.shuffleSeed,
    attempts: 1,
    scores: isComplete
      ? {
          us: view.state.teamScores[0],
          them: view.state.teamScores[1],
          won: view.state.teamScores[0] > view.state.teamScores[1],
        }
      : null,
  };
}

export function buildGameSessionView(
  input: BuildGameSessionViewInput,
): GameSessionView {
  const playerInfo = input.view.players.find(
    (player) =>
      (player.sessionId ?? `player-${player.playerId}`) === input.sessionId,
  );
  const canAct =
    input.mode === 'live' &&
    !!playerInfo?.capabilities?.some(
      (capability) => capability.type === 'act-as-player',
    );
  const transitions = input.view.transitions.map((transition) => ({
    id: transition.id,
    label: transition.label,
    action: transition.action,
    newState: input.view.state,
  }));
  const viewProjection = createViewProjection(input.view.state, transitions, {
    isTestMode: input.testMode === true,
    canAct,
    isAIControlled:
      input.mode === 'replay'
        ? () => false
        : (player) =>
            input.view.players.find((entry) => entry.playerId === player)
              ?.controlType === 'ai',
    derived: input.view.derived,
    ...(playerInfo ? { viewingPlayerIndex: playerInfo.playerId } : {}),
  });

  const sessionView: GameSessionView = {
    matchId: input.matchId,
    mode: input.mode,
    stateRevision: input.stateRevision,
    paused: input.paused,
    bidRevealActive: input.bidRevealActive,
    aiEnabled: input.aiEnabled,
    view: input.view,
    viewProjection,
    gameState: input.view.state,
    currentPerspective: input.sessionId,
    availablePerspectives: input.view.players.map((player) => ({
      id: player.sessionId ?? `player-${player.playerId}`,
      label: player.name ? `${player.name}` : `P${player.playerId + 1}`,
    })),
    oneHand: buildOneHandState(input.view),
    findingSeed: input.findingSeed,
    sharePolicy: { allowed: false, reason: '' },
    savedMatch: input.savedMatch,
  };
  sessionView.sharePolicy = evaluateSharePolicy(sessionView);
  return sessionView;
}
